import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const KEY = 'thinkingworld.admin.session';
// Write token returned by the server on login; ContentContext reads it to
// authorize publishing. It is NOT a secret baked into the bundle — it only
// exists in the browser after a successful server-verified login.
export const TOKEN_KEY = 'thinkingworld.admin.token';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setSession(JSON.parse(raw));
    } catch {
      // ignore
    }
    setReady(true);
  }, []);

  // Credentials are verified server-side by the /api/admin-login function, so
  // the real password never ships to the browser. On success the server
  // returns a write token we store for content publishing.
  const login = useCallback(async (username, password) => {
    try {
      const res = await fetch('/api/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok && data.token) {
        const next = { user: username, since: Date.now() };
        localStorage.setItem(KEY, JSON.stringify(next));
        localStorage.setItem(TOKEN_KEY, data.token);
        setSession(next);
        return { ok: true };
      }
      return { ok: false, error: data.error || 'Invalid username or password' };
    } catch {
      return { ok: false, error: 'Could not reach the login service. Check your connection.' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY);
    localStorage.removeItem(TOKEN_KEY);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ session, ready, login, logout, isAuthed: !!session }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
